import _ from 'lodash'
import { State } from './'
import { BaseAction, selectSubmissions } from './index'
import { CProfileState, ProfileState } from './profile'
import { MediaData } from '../components/MediaViewer'
import { ProfileToTagGrouped } from './partiposter'
import { REVIEW_MODAL_TYPE, CaptionReviewProps } from './review'
import { selectSubmitContentTaskUuids } from './tasks'
import {
    BaseExportSubmissionsPayload,
    RequestExportSubmissionsPayload,
    RequestExportBatchSubmissionsPayload,
    Submission,
    GetEditRequestHistoryListRequestPayload,
} from 'services/api/submissions'
import { MassList } from 'components/Submissions/ListApprovePayoutPopUp'
import { COUNTRY_ENUM, PLATFORM_ENUM } from 'services/api/constants'
import { GridFilterModel, GridSortItem } from '@mui/x-data-grid-pro'
import { OptionsState } from 'components/Shared/Inputs/CheckBoxMultiGroup'
import { getSortQueryText, FIELDS_TYPE } from 'components/utils/filterAndSort'
import { createSelector } from 'reselect'

export enum SUBMISSION_STATUS_ENUM {
    STARTED = 0,
    IN_PROGRESS = 1,
    REJECTED = 2,
    FINISHED = 3,
    PAID = 4,
    CLOSED = 99,
}

export type SubmissionStatusCode =
    | SUBMISSION_STATUS_ENUM.STARTED
    | SUBMISSION_STATUS_ENUM.IN_PROGRESS
    | SUBMISSION_STATUS_ENUM.REJECTED
    | SUBMISSION_STATUS_ENUM.FINISHED
    | SUBMISSION_STATUS_ENUM.PAID
    | SUBMISSION_STATUS_ENUM.CLOSED

export enum SUBMISSION_TASK_STATUS_ENUM {
    NOT_SUBMITTED = 0,
    PENDING = 1,
    APPROVED = 2,
    ACTION_REQUIRED = 3,
    EDITS_REQUESTED = 4,
    REJECTED = 5,
    COMPLETED = 99,
}

export type SubmissionTaskStatusCode =
    | SUBMISSION_TASK_STATUS_ENUM.NOT_SUBMITTED
    | SUBMISSION_TASK_STATUS_ENUM.PENDING
    | SUBMISSION_TASK_STATUS_ENUM.APPROVED
    | SUBMISSION_TASK_STATUS_ENUM.ACTION_REQUIRED
    | SUBMISSION_TASK_STATUS_ENUM.EDITS_REQUESTED
    | SUBMISSION_TASK_STATUS_ENUM.REJECTED
    | SUBMISSION_TASK_STATUS_ENUM.COMPLETED

export enum SubmissionProgressStatusCode {
    NOT_DONE = 0,
    DONE = 1,
}

export interface PartiposterProfile {
    uuid: string
    name: string
    age: number
    photo: string
    gender: string
    social_media_accounts: SocialMediaAccount[]
    interests: { id: number; label: string }[]
    personalities: { id: number; label: string }[] //todo: does it exist?
    tags: ProfileToTagGrouped[]
    tags_count: number
    occupation: number
    live_location: Record<string, number>
    work_location: Record<string, number>
    hangout_location: Record<string, number>
    phone: string
    country: COUNTRY_ENUM
}

export interface SocialMediaAccount {
    follower_count: number | null
    follower_number_tier?: number | null
    platform: PLATFORM_ENUM
    is_private?: boolean | null
    username: string | null
    profile_url?: string
}

export type VoteType = null | 'approve' | 'reject' | 'request_edits'

export type DiscussionState = {
    uuid: string
    cprofile: CProfileState
    updated_at: string
    created_at: string
    is_edited: boolean
    files: MediaData[]
    comment: string
}

export type VotesState = {
    approve: CProfileState[]
    reject: CProfileState[]
    request_edits: CProfileState[]
}

export interface SubmissionTaskState {
    uuid: string
    status: SubmissionTaskStatusCode
    data: {
        link_in_bio_url?: string
    }
    post_time_window: {
        start: string
        end: string
    }
}

export interface BatchSubmissionTaskState extends SubmissionTaskState {
    task_uuid: string
    current_progress: BatchSubmissionProgressState
    progress: {
        status: SubmissionProgressStatusCode
        step_uuid: string
        updated_at: string
        uuid: string
    }
}

interface VoteAndDiscussionState {
    latest_discussion: DiscussionState[]
    discussion_members: CProfileState[]
    discussion_count: number
    votes: VotesState
}

interface BaseSubmissionProgressState extends VoteAndDiscussionState {
    uuid: string
    status?: SubmissionProgressStatusCode
    data?: any
    reviewed_data?: any
    pending_action?: any
    submitted_at: string
    moved_out_of_pending_by?: CProfileState
    moved_out_of_pending_at?: string
    reviewed_by_system?: boolean
    comment: string
    resubmitted: boolean
}

export interface EditRequestHistoryState {
    data: any
    reviewed_data: any
    pending_action: any
    comment: string
    cporfile: {
        uuid: string
        name: string
        team_name: string
        is_active: boolean
    }
    updated_at: string
}

export interface BatchSubmissionProgressState extends BaseSubmissionProgressState {
    step_uuid: string
}

// TODO rename to SubmissionProgressState?
export interface SubmissionState extends BaseSubmissionProgressState {
    status?: SubmissionProgressStatusCode
    created_at?: string
    updated_at?: string
    submission_uuid: string //will be null if is batch current_progress; not exists in dropouts api & rejected api
    submission_task?: SubmissionTaskState //not exists in rejected api & submission-registration-dropouts api
    rejected_by?: string //exists in submission-rejected api & submission-registration-rejected api
    rejected_at?: string //exists in submission-rejected api & submission-registration-rejected api
    submission_status?: 'rejected' | 'dropped_out' // backend response for drop out and reject table
    error?: string
    tag_uuids?: string[]
    partiposterId: string
}

export interface BatchSubmissionState {
    uuid: string
    status: SubmissionStatusCode
    reviewed_data?: any
    created_at: string
    deleted_at: string | null
    submission_tasks: BatchSubmissionTaskState[]
    first_content_submitted_at?: string
    action_taken?: boolean
    ordering?: string
    is_inside_batch?: boolean
    partiposterId: string
    data?: any
    resubmitted?: boolean
    required_progress_count: number
    completed_progress_count: number
}

interface SubmissionsEntityState {
    [uuid: string]: SubmissionState
}

interface BatchSubmissionsEntityState {
    [uuid: string]: BatchSubmissionState
}

interface VoteAndDiscussionEntityState {
    [uuid: string]: VoteAndDiscussionState
}

interface FilterOption {
    results: {
        key: string
        options: string[]
    }[]
}

export type SubmissionsState = {
    batch: {
        count: number
        submissions: string[]
        byId: BatchSubmissionsEntityState
    }
    submissions: string[]
    byId: SubmissionsEntityState
    voteAndDiscussionBySubmissionId: VoteAndDiscussionEntityState
    massPayout: MassPayout
    editRequestHistory: EditRequestHistoryState[]
    filters: FilterOption[]
    submissionsFilterOptions: OptionsState[]
    submissionsFilterModel: GridFilterModel | null
    submissionsSortItem: GridSortItem | null
}
interface SaveMassPayoutProgress {
    uuid: string
    progress: MassPayoutProgress
}

export interface MassPayoutResult {
    id: string
    username: string
    message?: string
}
interface MassPayoutProgress {
    count: number
    total: number
}
interface MassPayout {
    [uuid: string]: {
        success: MassPayoutResult[]
        failed: MassPayoutResult[]
        progress: MassPayoutProgress
    }
}
const initialState: SubmissionsState = {
    batch: {
        count: 0,
        submissions: [],
        byId: {},
    },
    submissions: [],
    byId: {},
    voteAndDiscussionBySubmissionId: {},
    massPayout: {},
    editRequestHistory: [],
    filters: [],
    submissionsFilterOptions: [],
    submissionsFilterModel: null,
    submissionsSortItem: null,
}

export enum SubmissionsActions {
    SUBMISSIONS_PAGE_LIST_REQUEST = 'submissions/SUBMISSIONS_PAGE_LIST_REQUEST',
    SUBMISSIONS_PAGE_LIST_CANCEL = 'submissions/SUBMISSIONS_PAGE_LIST_CANCEL',
    BATCH_SUBMISSIONS_PAGE_LIST_REQUEST = 'submissions/BATCH_SUBMISSIONS_PAGE_LIST_REQUEST',
    BATCH_SUBMISSIONS_PAGE_LIST_CANCEL = 'submissions/BATCH_SUBMISSIONS_PAGE_LIST_CANCEL',
    SAVE_SUBMISSIONS_LIST = 'submissions/SAVE_SUBMISSIONS_LIST',
    DROPOUTS_LIST_REQUEST = 'submissions/DROPOUTS_LIST_REQUEST',
    SAVE_DROPOUTS_LIST = 'submissions/SAVE_DROPOUTS_LIST',
    REJECTED_LIST_REQUEST = 'submissions/REJECTED_LIST_REQUEST',
    SAVE_REJECTED_LIST = 'submissions/SAVE_REJECTED_LIST',
    REPLACE_SUBMISSIONS_LIST = 'submissions/REPLACE_SUBMISSIONS_LIST',
    OPTIMISTIC_REMOVE_SUBMISSIONS_LIST = 'submissions/OPTIMISTIC_REMOVE_SUBMISSIONS_LIST',
    UPDATE_CAPTION_REVIEW_IN_SUBMISSIONS_LIST = 'submissions/UPDATE_CAPTION_REVIEW_IN_SUBMISSIONS_LIST',
    REPLACE_BATCH_SUBMISSIONS_LIST = 'submissions/REPLACE_BATCH_SUBMISSIONS_LIST',
    UPDATE_CAPTION_REVIEW_IN_BATCH_SUBMISSIONS_LIST = 'submissions/UPDATE_CAPTION_REVIEW_IN_BATCH_SUBMISSIONS_LIST',
    REMOVE_FROM_LIST = 'submissions/REMOVE_FROM_LIST',
    REMOVE_TASK_FROM_BATCH_LIST = 'submissions/REMOVE_TASK_FROM_BATCH_LIST',
    REMOVE_FROM_BATCH_LIST = 'submissions/REMOVE_FROM_BATCH_LIST',
    CLEAR_SUBMISSIONS_LIST = 'submissions/CLEAR_SUBMISSIONS_LIST',
    REJECT_SUBMISSION = 'submissions/REJECT_SUBMISSION',
    APPROVE_SUBMISSION = 'submissions/APPROVE_SUBMISSION',
    REQUEST_EDITS_FOR_SUBMISSION = 'submissions/REQUEST_EDITS_FOR_SUBMISSION',
    UPDATE_COMPLETED_SCHEDULE = 'submissions/UPDATE_COMPLETED_SCHEDULE',
    GET_DISCUSS_SUBMISSION = 'submissions/GET_DISCUSSS_SUBMISSION',
    CREATE_DISCUSS_SUBMISSION = 'submissions/CREATE_DISCUSS_SUBMISSION',
    UPDATE_DISCUSS_SUBMISSION = 'submissions/UPDATE_DISCUSS_SUBMISSION',
    DELETE_DISCUSS_SUBMISSION = 'submissions/DELETE_DISCUSS_SUBMISSION',
    UPDATE_DISCUSSION_LIST = 'submissions/UPDATE_DISCUSSION_LIST',
    VOTE_SUBMISSION = 'submissions/VOTE_SUBMISSION',
    UPDATE_VOTE = 'submissions/UPDATE_VOTE',
    REVIEW_PENDING_ACTION = 'submissions/REVIEW_PENDING_ACTION',
    UPDATE_SUBMISSION_PERFORMANCE = 'submissions/UPDATE_SUBMISSION_PERFORMANCE',
    PAY_SUBMISSION = 'submissions/PAY_SUBMISSION',
    REJECT_SUBMISSION_PAYOUT = 'submissions/REJECT_SUBMISSION_PAYOUT',
    MASS_SUBMISSION = 'submissions/MASS_SUBMISSION',
    UPDATE_MASS_RESULT = 'submissions/UPDATE_MASS_RESULT',
    UPDATE_SCHEDULE = 'submissions/UPDATE_SCHEDULE',
    EXPORT_SUBMISSION_REQUEST = 'submissions/EXPORT_SUBMISSION_REQUEST',
    EXPORT_BATCH_SUBMISSIONS_REQUEST = 'submissions/EXPORT_BATCH_SUBMISSIONS_REQUEST',
    MASS_PAYOUT_SUBMISSION = 'submissions/MASS_PAYOUT_SUBMISSION',
    SAVE_MASS_PAYOUT_PROGRESS = 'submissions/SAVE_MASS_PAYOUT_PROGRESS',
    SAVE_MASS_PAYOUT_RESULT = 'submissions/SAVE_MASS_PAYOUT_RESULT',
    GET_EDIT_REQUEST_HISTORY_LIST = 'submissions/GET_EDIT_REQUEST_HISTORY_LIST',
    SAVE_EDIT_REQUEST_HISTORY_LIST = 'submissions/SAVE_EDIT_REQUEST_HISTORY_LIST',
    CLEAR_EDIT_REQUEST_HISTORY_LIST = 'submissions/CLEAR_EDIT_REQUEST_HISTORY_LIST',
    EXTEND_FIRST_STEP_DEADLINE_REQUEST = 'submissions/EXTEND_FIRST_STEP_DEADLINE_REQUEST',
    REVIEW_CAPTION = 'submissions/REVIEW_CAPTION',
    SET_SUBMISSIONS_FILTER_MODEL = 'submissions/SET_SUBMISSIONS_FILTER_MODEL',
    SET_SUBMISSIONS_FILTER_OPTIONS = 'submissions/SET_SUBMISSIONS_FILTER_OPTIONS',
    SET_SUBMISSIONS_SORT_ITEM = 'submissions/SET_SUBMISSIONS_SORT_ITEM',
    GET_SUBMISSIONS_FILTERS_SUCCESS = 'submissions/GET_SUBMISSIONS_FILTERS_SUCCESS',
    GET_DROPOUTS_AND_REJECTED = 'submissions/GET_DROPOUTS_AND_REJECTED',
    GET_DROPOUTS_AND_REJECTED_SUCCESS = 'submissions/GET_DROPOUTS_AND_REJECTED_SUCCESS',
    DROPOUTS_AND_REJECTED_CANCEL = 'submissions/DROPOUTS_AND_REJECTED_CANCEL',
    MASS_REJECT_ALL_SUBMISSION = 'submissions/MASS_REJECT_ALL_SUBMISSION',
}

export enum SUBMISSION_VIEW_ENUM {
    FOR_REVIEW = 'for_review',
    PENDING = 'pending',
    APPROVED = 'approved',
    COMPLETED = 'completed',
    EDITS_REQUESTED = 'edits_requested',
    BATCH_SUBMISSIONS = 'batch_submissions',
}

export type SubmissionStatusInOverview =
    | SUBMISSION_VIEW_ENUM.PENDING
    | SUBMISSION_VIEW_ENUM.FOR_REVIEW
    | SUBMISSION_VIEW_ENUM.EDITS_REQUESTED
    | SUBMISSION_VIEW_ENUM.COMPLETED

export enum TASK_VIEW_ENUM {
    BATCH_SUBMISSIONS = 'batch_submissions',
    REGISTRATION = 'registration',
    TASKS = 'tasks',
}

export interface SubmissionsListRequestPayload {
    loadMore?: boolean
    url?: string
    campaignUuid: string
    stepUuid?: string
    submissionView: SUBMISSION_VIEW_ENUM | null
    vote_type?: string
    keywords?: string
    tags?: string
    resubmitted?: string
}

export interface SubmissionsPageListRequestPayload {
    offset?: number
    url?: string
    campaignUuid: string
    stepUuid?: string
    submissionView: SUBMISSION_VIEW_ENUM | null
    vote_type?: string
    keywords?: string
    tags?: string
    resubmitted?: string
    onSettled?: () => void
}

export interface BatchSubmissionsPageListRequestPayload {
    offset?: number
    campaignUuid: string
    limit?: number
    ordering?: string
    keywords?: string
    filteringParams?: {
        [key: string]: string[] | string | undefined
    }
    onSettled?: () => void
}

export const getSubmissionsPageList = (payload: SubmissionsPageListRequestPayload) => ({
    payload,
    type: SubmissionsActions.SUBMISSIONS_PAGE_LIST_REQUEST,
})

export const getBatchSubmissionsPageList = (payload: BatchSubmissionsPageListRequestPayload) => ({
    payload,
    type: SubmissionsActions.BATCH_SUBMISSIONS_PAGE_LIST_REQUEST,
})

export interface DropoutsListRequestPayload {
    type: 'submission' | 'registration'
    loadMore?: boolean
    url?: string
    campaignUuid: string
    offset?: number
    keywords?: string
}

export const getDropoutsList = (payload: DropoutsListRequestPayload) => ({
    payload,
    type: SubmissionsActions.DROPOUTS_LIST_REQUEST,
})

export interface RejectedListRequestPayload {
    type: 'submission' | 'registration'
    loadMore?: boolean
    url?: string
    campaignUuid: string
    offset?: number
    keywords?: string
}

interface SetSortItemPayload {
    options:
        | (GridSortItem & {
              fieldLabel: string
              sortDirectionLabel: string
          })
        | null
}

interface SetFilterModelPayload {
    options: GridFilterModel | null
}

export const getRejectedList = (payload: RejectedListRequestPayload) => ({
    payload,
    type: SubmissionsActions.REJECTED_LIST_REQUEST,
})

export interface RemoveSubmissionFromListPayload {
    uuid: string
}

export const removeSubmissionFromList = (payload: RemoveSubmissionFromListPayload) => ({
    payload,
    type: SubmissionsActions.REMOVE_FROM_LIST,
})

export const removeBatchSubmissionFromList = (payload: RemoveSubmissionFromListPayload) => ({
    payload,
    type: SubmissionsActions.REMOVE_FROM_BATCH_LIST,
})

export interface RemoveTaskFromBatchSubmissionListPayload {
    submissionUuid: string
    taskUuid: string
}

export const removeTaskFromBatchSubmissionList = (payload: RemoveTaskFromBatchSubmissionListPayload) => ({
    payload,
    type: SubmissionsActions.REMOVE_TASK_FROM_BATCH_LIST,
})

export const saveSubmissionsList = (payload: { data?: SubmissionState[] }) => ({
    payload,
    type: SubmissionsActions.SAVE_SUBMISSIONS_LIST,
})

export const replaceSubmissionsList = (payload: { data?: SubmissionState[] }) => ({
    payload,
    type: SubmissionsActions.REPLACE_SUBMISSIONS_LIST,
})

// to improve user experience, which only update state directly after success (not requesting from api)
export const optimisticRemoveSubmissionFromState = (payload: {
    submissionProgressUuid: string
    submissionUuid?: string
    submissionTaskUuid?: string
}) => ({
    payload,
    type: SubmissionsActions.OPTIMISTIC_REMOVE_SUBMISSIONS_LIST,
})

export const updateCaptionReviewInSubmissionsList = (payload: {
    data?: CaptionReviewProps
    submissionProgressUuid: string
}) => ({
    payload,
    type: SubmissionsActions.UPDATE_CAPTION_REVIEW_IN_SUBMISSIONS_LIST,
})

export const replaceBatchSubmissionsList = (payload: { count?: number | null; data?: BatchSubmissionState[] }) => ({
    payload,
    type: SubmissionsActions.REPLACE_BATCH_SUBMISSIONS_LIST,
})

export const updateCaptionReviewInBatchSubmissionsList = (payload: {
    data?: CaptionReviewProps
    submissionProgressUuid: string
}) => ({
    payload,
    type: SubmissionsActions.UPDATE_CAPTION_REVIEW_IN_BATCH_SUBMISSIONS_LIST,
})

export const clearSubmissionsList = () => ({
    type: SubmissionsActions.CLEAR_SUBMISSIONS_LIST,
})

export const cancelSubmissionPageList = () => ({
    type: SubmissionsActions.SUBMISSIONS_PAGE_LIST_CANCEL,
})

export const cancelBatchSubmissionPageList = () => ({
    type: SubmissionsActions.BATCH_SUBMISSIONS_PAGE_LIST_CANCEL,
})

export const setSubmissionsFilterModel = (payload: SetFilterModelPayload) => ({
    payload,
    type: SubmissionsActions.SET_SUBMISSIONS_FILTER_MODEL,
})

export const setSubmissionsFilterOptions = (payload: OptionsState[]) => ({
    payload,
    type: SubmissionsActions.SET_SUBMISSIONS_FILTER_OPTIONS,
})

export const setSubmissionsSortItem = (payload: SetSortItemPayload) => ({
    payload,
    type: SubmissionsActions.SET_SUBMISSIONS_SORT_ITEM,
})

export const getSubmissionsFilterSuccess = (data: FilterOption[]) => ({
    type: SubmissionsActions.GET_SUBMISSIONS_FILTERS_SUCCESS,
    payload: data,
})

export interface RejectSubmissionPayload {
    campaignUuid: string
    submissionUuid?: string
    submissionProgressUuid?: string
    submissionTaskUuid?: string
    reason?: string
    type?: REVIEW_MODAL_TYPE
    getSubmissionListPayload: Omit<SubmissionsPageListRequestPayload, 'campaignUuid'>
    enableOptimisticUpdate?: boolean
    onSuccess?: () => void
}

export const rejectSubmission = (payload: RejectSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.REJECT_SUBMISSION,
})

export interface ApproveSubmissionPayload {
    campaignUuid: string
    submissionProgressUuid: string
    submissionTaskUuid?: string
    submissionUuid?: string
    data: {
        files?: MediaData[]
        caption?: string
        url?: string
        posting_time?: string
        link_in_bio_url?: string
    }
    comment?: string
    getSubmissionListPayload: Omit<SubmissionsPageListRequestPayload, 'campaignUuid'>
    enableOptimisticUpdate?: boolean
    onSuccess?: () => void
}

export const approveSubmission = (payload: ApproveSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.APPROVE_SUBMISSION,
})

export interface UpdateCompletedSchedulePayload {
    submissionUuid: string
    data: {
        post_time_window: {
            start: string
            end: string
        }
    }
}

export const updateCompletedSchedule = (payload: UpdateCompletedSchedulePayload) => ({
    payload,
    type: SubmissionsActions.UPDATE_COMPLETED_SCHEDULE,
})

interface UpdateSchedulePayload {
    data: SubmissionState | undefined
    submissionUuid: string
}

export const updateSchedule = (payload: UpdateSchedulePayload) => ({
    payload,
    type: SubmissionsActions.UPDATE_SCHEDULE,
})

export interface MassSubmissionPayload {
    action_name: string
    campaignUuid: string
    uuids: string[]
    comment: string
    post_time_window?: { start: string; end: string }
    data?: any
    getSubmissionListPayload: Omit<SubmissionsPageListRequestPayload, 'campaignUuid'>
}

export interface MassError {
    uuid: string
    error: string
}

export interface MassSubmissionResult extends MassSubmissionPayload {
    success_count?: number
    errors?: MassError[]
}

export const massSubmission = (payload: MassSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.MASS_SUBMISSION,
})

export const updateMassResult = (payload: MassSubmissionResult) => ({
    payload,
    type: SubmissionsActions.UPDATE_MASS_RESULT,
})

export interface MassRejectAllSubmissionPayload {
    campaignUuid: string
    uuids: string[]
    comment: string
    getSubmissionListPayload: Omit<SubmissionsPageListRequestPayload, 'campaignUuid'>
}

export const massRejectAllSubmission = (payload: MassRejectAllSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.MASS_REJECT_ALL_SUBMISSION,
})

export interface DiscussSubmissionPayload {
    campaignUuid: string
    submissionUuid: string
    discussionUuid?: string
    files?: MediaData[]
    comment?: string
}

export const getDiscussion = (payload: DiscussSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.GET_DISCUSS_SUBMISSION,
})

export const createDiscussSubmission = (payload: DiscussSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.CREATE_DISCUSS_SUBMISSION,
})

export const updateDiscussSubmission = (payload: DiscussSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.UPDATE_DISCUSS_SUBMISSION,
})

export const deleteDiscussSubmission = (payload: DiscussSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.DELETE_DISCUSS_SUBMISSION,
})

export interface UpdateDiscussionPayload extends DiscussSubmissionPayload {
    actionType?: 'get' | 'create' | 'update' | 'delete'
    data?: SubmissionState
}

export const updateDiscussList = (payload: UpdateDiscussionPayload) => ({
    payload,
    type: SubmissionsActions.UPDATE_DISCUSSION_LIST,
})

export interface VoteSubmissionPayload {
    campaignUuid: string
    submissionProgressUuid: string
    vote_type: VoteType
    profile?: ProfileState
}

export const voteSubmission = (payload: VoteSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.VOTE_SUBMISSION,
})

interface UpdateVotePayload {
    data: VotesState | undefined
    submissionProgressUuid: string
}

export const updateVote = (payload: UpdateVotePayload) => ({
    payload,
    type: SubmissionsActions.UPDATE_VOTE,
})

export interface RequestEditsForSubmissionPayload {
    campaignUuid: string
    submissionProgressUuid: string
    submissionTaskUuid?: string
    submissionUuid?: string
    reason?: string
    getSubmissionListPayload: Omit<SubmissionsPageListRequestPayload, 'campaignUuid'>
    enableOptimisticUpdate?: boolean
    data?: {
        files?: MediaData[]
        caption?: string
    }
    onSuccess?: () => void
}

export const requestEditsForSubmission = (payload: RequestEditsForSubmissionPayload) => ({
    payload,
    type: SubmissionsActions.REQUEST_EDITS_FOR_SUBMISSION,
})

export interface ReviewPendingActionPayload {
    campaignUuid: string
    submissionUuid: string
    actionType: 'approve' | 'reject'
    getSubmissionListPayload: Omit<SubmissionsPageListRequestPayload, 'campaignUuid'>
    onSuccess?: () => void
}

export const reviewPendingAction = (payload: ReviewPendingActionPayload) => ({
    payload,
    type: SubmissionsActions.REVIEW_PENDING_ACTION,
})

export interface UpdateSubmissionPerformancePayload {
    campaignUuid: string
    submissionUuid: string
    submissionTasks: any[]
}

export const updateSubmissionPerformance = (payload: UpdateSubmissionPerformancePayload) => ({
    payload,
    type: SubmissionsActions.UPDATE_SUBMISSION_PERFORMANCE,
})

export interface PaySubmissionPayload {
    campaignUuid: string
    submissionUuid: string
}
export interface MassPaySubmissionPayload {
    campaignUuid: string
    platform: PLATFORM_ENUM
    usernames: MassList
    moveNextStep: (step: number) => void
}
export interface MassPayoutRequest {
    usernames: string[]
    platform: PLATFORM_ENUM
    campaignUuid: string
    usernameToChunk: MassList
    moveNextStep: (step: number) => void
}
export const paySubmission = (payload: PaySubmissionPayload) => ({
    payload,
    type: SubmissionsActions.PAY_SUBMISSION,
})
export const massPaySubmission = (payload: MassPaySubmissionPayload) => ({
    payload,
    type: SubmissionsActions.MASS_PAYOUT_SUBMISSION,
})

export interface RejectSubmissionPayoutPayload {
    campaignUuid: string
    submissionUuid: string
}

export const rejectSubmissionPayout = (payload: RejectSubmissionPayoutPayload) => ({
    payload,
    type: SubmissionsActions.REJECT_SUBMISSION_PAYOUT,
})

// Helper to create export action (reduces duplication)
const createExportAction = <T extends BaseExportSubmissionsPayload>(payload: T, actionType: SubmissionsActions) => ({
    payload,
    type: actionType,
})

export const exportSubmissions = (payload: RequestExportSubmissionsPayload) =>
    createExportAction(payload, SubmissionsActions.EXPORT_SUBMISSION_REQUEST)

export const exportBatchSubmissions = (payload: RequestExportBatchSubmissionsPayload) =>
    createExportAction(payload, SubmissionsActions.EXPORT_BATCH_SUBMISSIONS_REQUEST)

export const saveMassPayoutProgress = (payload: SaveMassPayoutProgress) => ({
    payload,
    type: SubmissionsActions.SAVE_MASS_PAYOUT_PROGRESS,
})
export const saveMassPayoutResult = (payload: {
    campaignUuid: string
    success: MassPayoutResult[]
    failed: MassPayoutResult[]
}) => ({
    payload,
    type: SubmissionsActions.SAVE_MASS_PAYOUT_RESULT,
})

export const getEditRequestHistoryList = (payload: GetEditRequestHistoryListRequestPayload) => ({
    payload,
    type: SubmissionsActions.GET_EDIT_REQUEST_HISTORY_LIST,
})

export const saveEditRequestHistoryList = (payload: EditRequestHistoryState[]) => ({
    payload,
    type: SubmissionsActions.SAVE_EDIT_REQUEST_HISTORY_LIST,
})

export const clearEditRequestHistoryList = () => ({
    type: SubmissionsActions.CLEAR_EDIT_REQUEST_HISTORY_LIST,
})

export interface ExtendFirstStepDeadlineRequestPayload {
    stepUuid: string
    stepName: string
    newStep1EndDate: string
    onSuccessCallback?: () => void
}

export const extendFirstStepDeadline = (payload: ExtendFirstStepDeadlineRequestPayload) => ({
    payload,
    type: SubmissionsActions.EXTEND_FIRST_STEP_DEADLINE_REQUEST,
})

export interface ReviewCaptionRequestPayload {
    selectedSubmissionView: SUBMISSION_VIEW_ENUM
    submissionProgressUuid: string
    caption: string
    updateLoadingCallback: (uuid: string) => void
    retrySuccessCallback: () => void
}

export const reviewCaption = (payload: ReviewCaptionRequestPayload) => ({
    payload,
    type: SubmissionsActions.REVIEW_CAPTION,
})

export const getDropoutsAndRejectedList = (payload: { campaignUuid: string; offset?: number; keywords?: string }) => ({
    payload,
    type: SubmissionsActions.GET_DROPOUTS_AND_REJECTED,
})

export const cancelDropoutsAndRejectedList = () => ({
    type: SubmissionsActions.DROPOUTS_AND_REJECTED_CANCEL,
})

export const submissionsReducer = (state: SubmissionsState = initialState, action: BaseAction) => {
    switch (action.type) {
        case SubmissionsActions.SAVE_SUBMISSIONS_LIST: {
            const { data } = action.payload
            const uuids = data.map((submission: SubmissionState) => submission.uuid)
            const byId = _.keyBy(data, 'uuid')
            const voteAndDiscussionBySubmissionId: any = {}
            data.forEach((submission: SubmissionState) => {
                voteAndDiscussionBySubmissionId[submission.uuid] = {
                    latest_discussion: submission.latest_discussion,
                    discussion_members: submission.discussion_members,
                    discussion_count: submission.discussion_count,
                    votes: submission.votes,
                }
            })

            return Object.assign({}, state, {
                ...state,
                submissions: _.uniq([...state.submissions, ...uuids]),
                byId: {
                    ...state.byId,
                    ...byId,
                },
                voteAndDiscussionBySubmissionId: {
                    ...state.voteAndDiscussionBySubmissionId,
                    ...voteAndDiscussionBySubmissionId,
                },
            })
        }
        case SubmissionsActions.UPDATE_CAPTION_REVIEW_IN_SUBMISSIONS_LIST: {
            const { data, submissionProgressUuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    [submissionProgressUuid]: {
                        ...state.byId[submissionProgressUuid],
                        data: {
                            ...state.byId[submissionProgressUuid].data,
                            caption_review: {
                                ...state.byId[submissionProgressUuid].data.caption_review,
                                ...data,
                            },
                        },
                    },
                },
            })
        }
        case SubmissionsActions.REPLACE_SUBMISSIONS_LIST: {
            const { data } = action.payload
            const uuids = data.map((submission: Submission) => submission.uuid)
            const byId = _.keyBy(data, 'uuid')
            const voteAndDiscussionBySubmissionId: any = {}
            data.forEach((submission: SubmissionState) => {
                voteAndDiscussionBySubmissionId[submission.uuid] = {
                    latest_discussion: submission.latest_discussion,
                    discussion_members: submission.discussion_members,
                    discussion_count: submission.discussion_count,
                    votes: submission.votes,
                }
            })
            return Object.assign({}, state, {
                ...state,
                submissions: uuids,
                byId,
                voteAndDiscussionBySubmissionId,
            })
        }
        case SubmissionsActions.OPTIMISTIC_REMOVE_SUBMISSIONS_LIST: {
            const { submissionUuid, submissionProgressUuid, submissionTaskUuid } = action.payload
            const newState = Object.assign({}, state)

            if (state.batch.count > 0 && submissionUuid) {
                const currSubmission = newState.batch.byId[submissionUuid]
                if (currSubmission) {
                    newState.batch.byId[submissionUuid] = {
                        ...newState.batch.byId[submissionUuid],
                        submission_tasks: currSubmission.submission_tasks.filter(
                            task => task.uuid !== submissionTaskUuid,
                        ),
                    }
                }
            }

            if (state.submissions.length > 0) {
                newState.submissions = newState.submissions.filter(uuid => uuid !== submissionProgressUuid)
            }
            return newState
        }
        case SubmissionsActions.UPDATE_CAPTION_REVIEW_IN_BATCH_SUBMISSIONS_LIST: {
            const { data, submissionProgressUuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                batch: {
                    byId: {
                        ...state.batch.byId,
                        [submissionProgressUuid]: {
                            ...state.batch.byId[submissionProgressUuid],
                            data: {
                                ...state.batch.byId[submissionProgressUuid].data,
                                caption_review: {
                                    ...state.batch.byId[submissionProgressUuid].data.caption_review,
                                    ...data,
                                },
                            },
                        },
                    },
                },
            })
        }
        case SubmissionsActions.REPLACE_BATCH_SUBMISSIONS_LIST: {
            const { count, data } = action.payload
            //judge
            const uuids = data.map((submission: BatchSubmissionState) => submission.uuid)
            const byId = _.keyBy(data, 'uuid')
            const voteAndDiscussionBySubmissionId: any = {}
            data.forEach((submission: BatchSubmissionState) => {
                submission.submission_tasks.forEach((submissionTask: BatchSubmissionTaskState) => {
                    if (submissionTask.current_progress) {
                        const { uuid, latest_discussion, discussion_members, discussion_count, votes } =
                            submissionTask.current_progress
                        voteAndDiscussionBySubmissionId[uuid] = {
                            latest_discussion,
                            discussion_members,
                            discussion_count,
                            votes,
                        }
                    }
                })
            })
            return Object.assign({}, state, {
                ...state,
                batch: {
                    count: count,
                    submissions: uuids,
                    byId,
                },
                voteAndDiscussionBySubmissionId,
            })
        }
        case SubmissionsActions.REMOVE_FROM_LIST: {
            const { uuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                submissions: _.without(state.submissions, uuid),
            })
        }
        case SubmissionsActions.REMOVE_TASK_FROM_BATCH_LIST: {
            const { submissionUuid, taskUuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                batch: {
                    ...state.batch,
                    byId: {
                        ...state.batch.byId,
                        [submissionUuid]: {
                            ...state.batch.byId[submissionUuid],
                            submission_tasks: state.batch.byId[submissionUuid].submission_tasks.filter(
                                (submission_task: BatchSubmissionTaskState) => submission_task.task_uuid != taskUuid,
                            ),
                        },
                    },
                },
            })
        }
        case SubmissionsActions.REMOVE_FROM_BATCH_LIST: {
            const { uuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                batch: {
                    ...state.batch,
                    submissions: _.without(state.batch.submissions, uuid),
                },
            })
        }
        case SubmissionsActions.UPDATE_VOTE: {
            const { data, submissionProgressUuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                voteAndDiscussionBySubmissionId: {
                    ...state.voteAndDiscussionBySubmissionId,
                    [submissionProgressUuid]: {
                        ...state.voteAndDiscussionBySubmissionId[submissionProgressUuid],
                        votes: data,
                    },
                },
            })
        }
        case SubmissionsActions.UPDATE_DISCUSSION_LIST: {
            const { submissionUuid, discussionUuid, actionType, data } = action.payload
            if (actionType == 'create') {
                return Object.assign({}, state, {
                    ...state,
                    voteAndDiscussionBySubmissionId: {
                        ...state.voteAndDiscussionBySubmissionId,
                        [submissionUuid]: {
                            ...state.voteAndDiscussionBySubmissionId[submissionUuid],
                            latest_discussion: [
                                ...state.voteAndDiscussionBySubmissionId[submissionUuid].latest_discussion,
                                data,
                            ],
                        },
                    },
                })
            } else if (actionType == 'update') {
                return Object.assign({}, state, {
                    ...state,
                    voteAndDiscussionBySubmissionId: {
                        ...state.voteAndDiscussionBySubmissionId,
                        [submissionUuid]: {
                            ...state.voteAndDiscussionBySubmissionId[submissionUuid],
                            latest_discussion: state.voteAndDiscussionBySubmissionId[
                                submissionUuid
                            ].latest_discussion.map(item => {
                                if (item.uuid == data.uuid) {
                                    return data
                                }
                                return item
                            }),
                        },
                    },
                })
            } else if (actionType == 'delete') {
                return Object.assign({}, state, {
                    ...state,
                    voteAndDiscussionBySubmissionId: {
                        ...state.voteAndDiscussionBySubmissionId,
                        [submissionUuid]: {
                            ...state.voteAndDiscussionBySubmissionId[submissionUuid],
                            latest_discussion: state.voteAndDiscussionBySubmissionId[
                                submissionUuid
                            ].latest_discussion.filter(item => {
                                if (discussionUuid) return item.uuid != discussionUuid
                                return true
                            }),
                        },
                    },
                })
            } else if (actionType == 'get') {
                const sortResults = data.results.sort((a: any, b: any) => {
                    return a.created_at > b.created_at ? 1 : -1
                })
                return Object.assign({}, state, {
                    ...state,
                    voteAndDiscussionBySubmissionId: {
                        ...state.voteAndDiscussionBySubmissionId,
                        [submissionUuid]: {
                            ...state.voteAndDiscussionBySubmissionId[submissionUuid],
                            latest_discussion: sortResults,
                        },
                    },
                })
            }
            return state
        }
        case SubmissionsActions.CLEAR_SUBMISSIONS_LIST: {
            return Object.assign({}, state, {
                batch: {
                    count: 0,
                    submissions: [],
                    byId: {},
                },
                submissions: [],
                byId: {},
                voteAndDiscussionBySubmissionId: {},
            })
        }
        case SubmissionsActions.UPDATE_MASS_RESULT: {
            const { data } = action.payload
            const successSubmissionIds = action.payload.uuids.filter((uuid: string) => {
                if (data.errors.length > 0) {
                    return !data.errors.some((error: any) => error.uuid == uuid)
                }
                return true
            })
            const updatedState = Object.assign({}, state, {
                ...state,
                byId: data.errors.reduce(
                    (memo: SubmissionsEntityState, error: MassError) =>
                        Object.assign({}, memo, { [error.uuid]: { ...state.byId[error.uuid], error: error.error } }),
                    state.byId,
                ),
            })

            return Object.assign({}, updatedState, {
                ...updatedState,
                submissions: updatedState.submissions.filter((uuid: string) => !successSubmissionIds.includes(uuid)),
                byId: Object.keys(updatedState.byId)
                    .filter(uuid => !successSubmissionIds.includes(uuid))
                    .reduce((memo: SubmissionsEntityState, uuid: string) => {
                        return { ...memo, [uuid]: updatedState.byId[uuid] }
                    }, {}),
            })
        }
        case SubmissionsActions.UPDATE_SCHEDULE: {
            const { data, submissionUuid } = action.payload
            return Object.assign({}, state, {
                ...state,
                byId: {
                    ...state.byId,
                    [submissionUuid]: {
                        ...state.byId[submissionUuid],
                        submission_task: data.submission_task,
                    },
                },
            })
        }
        case SubmissionsActions.SAVE_MASS_PAYOUT_PROGRESS:
            const { uuid, progress } = action.payload
            return {
                ...state,
                massPayout: {
                    ...state.massPayout,
                    [uuid]: {
                        ...state.massPayout[uuid],
                        progress: progress,
                    },
                },
            }
        case SubmissionsActions.SAVE_MASS_PAYOUT_RESULT:
            const { campaignUuid, success, failed } = action.payload
            return {
                ...state,
                massPayout: {
                    ...state.massPayout,
                    [campaignUuid]: {
                        ...state.massPayout[campaignUuid],
                        success: success,
                        failed: failed,
                    },
                },
            }
        case SubmissionsActions.SAVE_EDIT_REQUEST_HISTORY_LIST:
            return Object.assign({}, state, {
                ...state,
                editRequestHistory: [...action.payload],
            })
        case SubmissionsActions.CLEAR_EDIT_REQUEST_HISTORY_LIST:
            return Object.assign({}, state, {
                ...state,
                editRequestHistory: [],
            })
        case SubmissionsActions.SET_SUBMISSIONS_FILTER_MODEL: {
            const { options } = action.payload
            return {
                ...state,
                submissionsFilterModel: options,
            }
        }
        case SubmissionsActions.SET_SUBMISSIONS_FILTER_OPTIONS: {
            return {
                ...state,
                submissionsFilterOptions: action.payload,
            }
        }
        case SubmissionsActions.SET_SUBMISSIONS_SORT_ITEM: {
            const { options } = action.payload || {}
            return {
                ...state,
                submissionsSortItem: options,
            }
        }
        case SubmissionsActions.GET_SUBMISSIONS_FILTERS_SUCCESS: {
            return {
                ...state,
                filters: action.payload,
            }
        }
        default:
            return state
    }
}

export const selectList = (state: SubmissionsState) => state.submissions.map((uuid: string) => state.byId[uuid])

const selectBatch = (state: State) => state.submissions.batch

export const selectBatchSubmissionsList = (state: State) =>
    selectBatch(state).submissions.map((uuid: string) => selectBatch(state).byId[uuid])

export const selectBatchCount = (state: State) => selectBatch(state).count

export const selectLatestVoteAndDiscussion = (uuid: string) => (state: State) =>
    selectSubmissions(state).voteAndDiscussionBySubmissionId[uuid]

export const selectIsPendingSubmitSubmissionExist = (state: State) => {
    const submitContentTaskUuids = selectSubmitContentTaskUuids(state)
    return selectBatchSubmissionsList(state).some((submission: BatchSubmissionState) => {
        return submission.submission_tasks.some(
            (t: BatchSubmissionTaskState) =>
                t.status == 1 &&
                t.current_progress?.pending_action == null &&
                submitContentTaskUuids.includes(t.task_uuid),
        )
    })
}

export const selectIsPendingSubmitTaskExist = (submissionUuid: string) => (state: State) => {
    const submitContentTaskUuids = selectSubmitContentTaskUuids(state)
    return (
        selectBatch(state).byId[submissionUuid] &&
        selectBatch(state).byId[submissionUuid].submission_tasks.some(
            (t: BatchSubmissionTaskState) => t.status != 99 && submitContentTaskUuids.includes(t.task_uuid),
        )
    )
}
export const selectMassPayoutProgress = (state: State, uuid: string) => {
    if (!state.submissions.massPayout[uuid]) {
        return {
            count: 0,
            total: 0,
        }
    } else return state.submissions.massPayout[uuid].progress
}
export const selectMassPayoutSuccess = (state: State, uuid: string) => {
    if (!state.submissions.massPayout[uuid] || !state.submissions.massPayout[uuid].success) {
        return []
    } else return state.submissions.massPayout[uuid].success
}
export const selectMassPayoutFailed = (state: State, uuid: string) => {
    if (!state.submissions.massPayout[uuid] || !state.submissions.massPayout[uuid].failed) {
        return []
    } else return state.submissions.massPayout[uuid].failed
}
export const selectEditRequestHistory = (state: State) => state.submissions.editRequestHistory
export const selectSubmissionFilters = (state: State) => state.submissions.filters
export const selectSubmissionsFilterOptions = (state: State) => state.submissions.submissionsFilterOptions
export const selectSubmissionsFilterModel = (state: State) => state.submissions.submissionsFilterModel
export const selectSubmissionsSortItem = (state: State) => state.submissions.submissionsSortItem

export const selectSelectedSubmissionsSortItem = createSelector(
    [selectSubmissionsSortItem],
    (gridSortItem): string | null => {
        if (gridSortItem && gridSortItem.field) {
            return getSortQueryText(gridSortItem.field, gridSortItem.sort || 'desc')
        } else {
            return null
        }
    },
)

export const selectBatchSubmissionsFilteringParams = createSelector([selectSubmissionsFilterModel], filterModel => {
    if (!filterModel) return {}

    const { items } = filterModel as GridFilterModel
    if (!items) {
        return {}
    }
    return items.reduce((acc: { [key: string]: string | string[] | undefined }, curr) => {
        const columnField = curr.columnField
        if (!columnField || !curr.value) return acc

        if (columnField === FIELDS_TYPE.GENDER) {
            return {
                ...acc,
                [columnField]: acc[columnField] ? `${acc[columnField]},${curr.value}` : curr.value,
            }
        } else if (
            columnField === FIELDS_TYPE.IG_FOLLOWERS ||
            columnField === FIELDS_TYPE.FB_FRIENDS ||
            columnField === FIELDS_TYPE.TIKTOK_FOLLOWERS ||
            columnField === FIELDS_TYPE.TWITTER_FOLLOWERS ||
            columnField === FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS
        ) {
            return {
                ...acc,
                social_media_follower_number_tier: acc.social_media_follower_number_tier
                    ? `${acc.social_media_follower_number_tier},${curr.value}`
                    : curr.value,
            }
        } else if (columnField === FIELDS_TYPE.PARTIPOSTER_TAGS) {
            return {
                ...acc,
                tags: acc.tags ? `${acc.tags},${curr.value}` : curr.value,
            }
        } else if (columnField === FIELDS_TYPE.COMPLETED_PROGRESS_COUNT) {
            return {
                ...acc,
                completed_progress_count: acc.completed_progress_count
                    ? `${acc.completed_progress_count},${curr.value}`
                    : curr.value,
            }
        }
        return acc
    }, {})
})
